import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Calendar, MapPin, PoundSterling, User, Phone, Mail, CalendarCheck } from "lucide-react";
import { useLocation } from "wouter";
import { CardActionsDropdown } from "@/components/card-actions-dropdown";
import type { ExternalEvent, Venue } from "@shared/schema";
import { format } from "date-fns";

interface ExternalEventCardProps {
  event: ExternalEvent & { venue?: Venue };
  organisationId: number;
  onEdit: () => void;
  onDelete: () => void;
  onCreateBooking: () => void;
}

export function ExternalEventCard({ event, organisationId, onEdit, onDelete, onCreateBooking }: ExternalEventCardProps) {
  const [, setLocation] = useLocation();
  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "tentative":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatBudget = (budget?: number | null) => {
    if (!budget) return null;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(budget / 100);
  };

  return (
    <Card 
      className="bg-white/20 backdrop-blur-xl border-white/30 hover:bg-white/30 transition-all duration-300 group cursor-pointer flex flex-col h-full"
      onClick={(e) => {
        // Don't navigate if clicking on buttons
        if ((e.target as HTMLElement).closest('button')) return;
        setLocation(`/organisations/${organisationId}/events/${event.id}/summary`);
      }}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-slate-900">
              {event.name}
            </CardTitle>
            <CardDescription className="mt-1">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-3 w-3" />
                <span>{event.venue?.name || 'No venue'}</span>
              </div>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(event.status)}>
              {event.status}
            </Badge>
            {/* Desktop: Individual buttons */}
            <div className="hidden sm:flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                onClick={onEdit}
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDelete}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {/* Mobile: Dropdown menu */}
            <div className="sm:hidden">
              <CardActionsDropdown
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          {/* Date and Time */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(event.date), "PPP 'at' p")}</span>
          </div>

          {/* Venue Address */}
          {event.venue && (
            <div className="flex items-start gap-2 text-sm text-slate-600">
              <MapPin className="h-4 w-4 mt-0.5" />
              <span className="flex-1">
                {event.venue.address}
                {event.venue.city && `, ${event.venue.city}`}
                {event.venue.state && `, ${event.venue.state}`}
                {event.venue.zipCode && ` ${event.venue.zipCode}`}
              </span>
            </div>
          )}

          {/* Budget */}
          {event.budget && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <PoundSterling className="h-4 w-4" />
              <span>Budget: {formatBudget(event.budget)}</span>
            </div>
          )}

          {/* Contact Info */}
          {(() => {
            const contactName = event.useVenueContact ? event.venue?.contactName : event.contactName;
            const contactEmail = event.useVenueContact ? event.venue?.contactEmail : event.contactEmail;
            const contactPhone = event.useVenueContact ? event.venue?.contactPhone : event.contactPhone;
            
            if (!contactName && !contactEmail && !contactPhone) return null;
            
            return (
              <div className="pt-2 border-t border-white/20 space-y-1">
                {contactName && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <User className="h-3 w-3" />
                    <span>{contactName}</span>
                  </div>
                )}
                {contactEmail && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-3 w-3" />
                    <a 
                      href={`mailto:${contactEmail}`}
                      className="text-blue-600 hover:underline"
                    >
                      {contactEmail}
                    </a>
                  </div>
                )}
                {contactPhone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="h-3 w-3" />
                    <a 
                      href={`tel:${contactPhone}`}
                      className="text-blue-600 hover:underline"
                    >
                      {contactPhone}
                    </a>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Notes */}
          {event.notes && (
            <p className="text-sm text-slate-600 italic line-clamp-2">
              {event.notes}
            </p>
          )}
        </div>

        {/* Action Button - Always at bottom */}
        {event.status !== "cancelled" && event.status !== "completed" && (
          <Button
            onClick={onCreateBooking}
            className="w-full bg-green-600 hover:bg-green-700 text-white mt-4"
            size="sm"
          >
            <CalendarCheck className="h-4 w-4 mr-2" />
            Create Booking
          </Button>
        )}
      </CardContent>
    </Card>
  );
}